import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ColumnFiltersState, OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import {
  useTable,
  tableFeatures,
  rowSortingFeature,
  sortFn_basic,
  createSortedRowModel,
  createFilteredRowModel,
  globalFilteringFeature,
  columnFilteringFeature,
  createFacetedRowModel,
  columnFacetingFeature,
  createFacetedMinMaxValues,
  createFacetedUniqueValues,
  rowPaginationFeature,
  metaHelper,
  rowSelectionFeature,
  createPaginatedRowModel,
  FlexRender,
  columnVisibilityFeature,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertPopover from 'components/HubertUI/HubertPopover';
import DebouncedInput from 'components/common/DebouncedInput';
import IsAxiosError from 'components/common/IsAxiosError';
import SkeletonTable from 'components/common/SkeletonTable/SkeletonTable';
import { useQueryParameters } from 'hooks/useQueryParameters';
import { ChevronDown, ChevronsUpDown, ChevronUp, Columns3, RefreshCw } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toggleRefetchJobApplicationsTableData } from 'rtk/layout/LayoutControlSlice';
import type { TPaginationResult } from 'rtk/query/apis/api-common.types';
import type { useFindPaginatedJobApplicationsQuery } from 'rtk/query/apis/applications';
import type { useFindPaginatedCandidatesQuery } from 'rtk/query/apis/candidates';
import type { useFindPaginatedClientsQuery } from 'rtk/query/apis/clients';
import type { useFindPaginatedInterviewsQuery } from 'rtk/query/apis/interviews';
import type { useFindPaginatedJobScoringQuery, useFindPaginatedJobsQuery } from 'rtk/query/apis/jobs';
import type { useFindPaginatedQuestionsQuery } from 'rtk/query/apis/questions';
import type {
  useFindPaginatedTemplateRevisionsQuery,
  useFindPaginatedTemplatesQuery,
  useGetPaginatedTemplateJobsQuery,
} from 'rtk/query/apis/templates';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import type { TExtendedApplication } from 'types/application.types';
import { EItemType } from 'types/general.types';
import { type TAnyClientQuestionType } from '@hubertai/screening-question-types';
import PaginationBar from './PaginationBar';
import { Filter, fuzzyFilter, type FuzzyFilterMeta } from './helpers/Helpers';
import './managerTableStyles.css';
import SelectedInterviewsActionsDropdown from './table-actions/SelectedInterviewActionsDialog';
import SelectedTemplateActionsDialog from './table-actions/SelectedTemplateActionsDialog';

interface Props extends ComponentProps<'div'> {
  itemType: EItemType;
  itemId?: string;
  defaultPageSize?: number;
  useData:
    | typeof useFindPaginatedCandidatesQuery
    | typeof useFindPaginatedClientsQuery
    | typeof useFindPaginatedJobsQuery
    | typeof useFindPaginatedTemplatesQuery
    | typeof useFindPaginatedJobScoringQuery
    | typeof useFindPaginatedJobApplicationsQuery
    | typeof useFindPaginatedQuestionsQuery
    | typeof useFindPaginatedTemplateRevisionsQuery
    | typeof useFindPaginatedInterviewsQuery
    | typeof useGetPaginatedTemplateJobsQuery;
  onUseData?: (data: TPaginationResult<any>) => void;
  columns: any;
  filterComponent?: ReactNode;
  filter?: {
    scoringStatus?: boolean;
    clientId?: string;
    [key: string]: any;
  };
  initialSortingState?: any[];
  sortingState?: SortingState;
  setSortingState?: OnChangeFn<SortingState>;
  containerClass?: string;

  onRowClick?: (currentRow: TExtendedApplication, rows: TExtendedApplication[]) => void;

  rowSelection: {
    enabled: boolean;
    onRowSelect?: (x: TAnyClientQuestionType[]) => void;
    isRowSelectionFinished?: boolean;
  };
}

const PaginatedManagerTable = ({
  itemType,
  itemId,
  defaultPageSize = 50,
  useData,
  onUseData,
  columns,
  filterComponent,
  filter,
  initialSortingState = [],
  sortingState,
  setSortingState,
  containerClass = '',
  onRowClick,
  rowSelection,
  ...props
}: Props) => {
  const { t } = useTranslation('PaginatedManagerTable');
  const refetchJobApplicationsTableData = useAppSelector((state) => state.Layout.refetchJobApplicationsTableData);
  const dispatch = useAppDispatch();

  const [results, setResults] = useState<TPaginationResult<any>>({
    // The current page number.
    pageIndex: 0,
    pageSize: defaultPageSize,
    totalItems: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    data: [],
  });
  const [_, setTotalItems] = useState(0);
  const [controlledPageCount, setPageCount] = useState<number>(defaultPageSize);
  const [controlledFilters, setControlledFilters] = useState<any>(filter);
  const [columnVisibility, setColumnVisibility] = useState({});
  const queryParams = useQueryParameters();

  const [sorting, setSorting] = useState<SortingState>(initialSortingState);
  const [selectedRows, setSelectedRows] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const { data, isFetching, isLoading, isError, error, refetch } = useData({
    pageIndex: results.pageIndex,
    pageSize: results.pageSize,
    filter: controlledFilters,
    sortingState,
  });

  useEffect(() => {
    const condition = !(isLoading || isFetching);
    if (condition && data) {
      console.log('PaginatedManagerTable data', data);
      setResults(data);
      setTotalItems(data.totalItems);
      setPageCount(data.totalPages);
      if (onUseData) {
        onUseData(data);
      }
    }
  }, [isLoading, isFetching, data, pageIndex, pageSize]);

  useEffect(() => {
    if (refetchJobApplicationsTableData) {
      refetch();
      dispatch(toggleRefetchJobApplicationsTableData({ refetch: false }));
    }
  }, [refetchJobApplicationsTableData]);

  useEffect(() => {
    if (rowSelection?.enabled && rowSelection.isRowSelectionFinished) {
      setSelectedRows({});
    }
  }, [rowSelection, rowSelection?.isRowSelectionFinished]);

  useEffect(() => {
    if (!sortingState) return;
    setResults((r) => ({ ...r, pageIndex: 0 }));
  }, [sortingState]);

  const defaultData = useMemo(() => [], []);

  const pagination = useMemo(
    () => ({
      pageIndex: results.pageIndex,
      pageSize: results.pageSize,
    }),
    [results],
  );

  const features = tableFeatures({
    columnFacetingFeature,
    columnFilteringFeature,
    columnVisibilityFeature,
    globalFilteringFeature,
    rowPaginationFeature,
    rowSelectionFeature,
    rowSortingFeature,
    facetedMinMaxValues: createFacetedMinMaxValues(),
    facetedRowModel: createFacetedRowModel(),
    facetedUniqueValues: createFacetedUniqueValues(),
    filteredRowModel: createFilteredRowModel(),
    paginatedRowModel: createPaginatedRowModel(),
    sortedRowModel: createSortedRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    sortFns: { sortFn_basic },
    filterMeta: metaHelper<FuzzyFilterMeta>(),
  });

  const table = useTable({
    features,
    data: results.data ?? defaultData,
    columns,
    // Pagination
    pageCount: controlledPageCount ?? -1,
    onPaginationChange: setPagination,
    manualPagination: true,

    // Visibility
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      pagination,
      rowSelection: selectedRows,
      sorting: sortingState ? sortingState : sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
    },

    // Selection
    onRowSelectionChange: setSelectedRows,
    manualSorting: !!sortingState,
    onSortingChange: sortingState ? setSortingState : setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,

    globalFilterFn: fuzzyFilter,
    enableRowSelection: true,

    debugTable: false,
  });

  useEffect(() => {
    setControlledFilters(filter);
    table.setPageIndex(0);
  }, [filter]);

  useEffect(() => {
    if (rowSelection?.enabled && rowSelection.onRowSelect) {
      if (itemType === EItemType.Questions) {
        const questions = table.getSelectedRowModel().flatRows.map((x) => x.original) as TAnyClientQuestionType[];
        rowSelection.onRowSelect(questions);
      }
    }
  }, [selectedRows]);

  if (isError && error) {
    if ('status' in error && error.status === 401) {
      return (
        <IsAxiosError
          error={{
            ...error,
            message: t('table.noPermission'),
          }}
        />
      );
    }

    return <IsAxiosError error={error} />;
  }

  const isBusy = isLoading || isFetching;

  return (
    <div {...props} className={clsx('flex h-full flex-col', 'p-2', containerClass)}>
      <div className={clsx('flex flex-row justify-end gap-2', 'p-2')}>
        {filterComponent}

        <div className={clsx('hubert-vertical-center flex justify-end gap-2', 'w-1/2')}>
          {itemType === EItemType.Interviews ? (
            <SelectedInterviewsActionsDropdown
              selectedInterviews={table.getSelectedRowModel().flatRows.map((x) => x.original)}
              jobId={itemId}
            />
          ) : null}

          {itemType === EItemType.Templates ? (
            <SelectedTemplateActionsDialog
              selectedTemplates={table.getSelectedRowModel().flatRows.map((x) => x.original)}
              onComplete={() => setSelectedRows({})}
            />
          ) : null}

          <HubertPopover>
            <HubertPopover.Trigger asChild>
              <HubertButton size={'sm'} variant={'secondary'}>
                <Columns3 />
              </HubertButton>
            </HubertPopover.Trigger>
            <HubertPopover.Content style={{ width: 360 }} side="left">
              <div className="px-1">
                <label>
                  <input
                    {...{
                      type: 'checkbox',
                      checked: table.getIsAllColumnsVisible(),
                      onChange: table.getToggleAllColumnsVisibilityHandler(),
                    }}
                  />{' '}
                  {t('table.toggleAll')}
                </label>
                {table.getAllLeafColumns().map((column) => {
                  return (
                    <div key={column.id} className="px-1">
                      <label>
                        <input
                          {...{
                            type: 'checkbox',
                            checked: column.getIsVisible(),
                            onChange: column.getToggleVisibilityHandler(),
                          }}
                        />{' '}
                        {/* TODO(i18n): column visibility dropdown renders column.id (developer identifier) instead of the translated column header label.
                            Fix requires adding meta: { label: t('...') } to every column definition across all paginated tables in the app.
                            Deferred — too broad for this PR's scope (touches Jobs, Clients, Candidates, Interviews, Dashboard).
                            See TanStack ColumnMeta for the intended pattern. */}
                        {column.id}
                      </label>
                    </div>
                  );
                })}
              </div>
            </HubertPopover.Content>
          </HubertPopover>

          <HubertButton
            variant={'outline'}
            size={'sm'}
            onClick={() => {
              refetch();
            }}
            loading={isBusy}
            icon={<RefreshCw />}
          />

          <DebouncedInput
            value={globalFilter ?? ''}
            onChange={(value) => setGlobalFilter(String(value))}
            className={clsx('w-32 focus:w-[70%]', 'transition-width transition-all duration-300')}
            placeholder={t('table.searchAllColumns')}
          />
        </div>
      </div>

      {isBusy ? (
        <div className={clsx('p-2', 'flex-1', 'overflow-auto')}>
          <SkeletonTable />
        </div>
      ) : (
        <Table className={clsx('flex-1')}>
          <TableHeader className={clsx('')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={clsx('sticky', 'top-0', 'bg-white')}
                      // @ts-ignore
                      {...header.column.columnDef.meta?.headerProps}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: canSort ? 'cursor-pointer select-none' : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          <div
                            className={clsx(
                              canSort ? 'hubert-vertical-center flex w-full flex-row justify-between' : '',
                              'text-xs',
                            )}
                          >
                            <FlexRender header={header} />

                            {canSort === true
                              ? ({
                                  asc: <ChevronUp size={16} />,
                                  desc: <ChevronDown size={16} />,
                                }[header.column.getIsSorted() as string] ?? <ChevronsUpDown size={16} />)
                              : null}
                          </div>

                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </tr>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              return (
                <TableRow
                  key={row.id}
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(
                        { rowId: row.id, ...row.original },
                        table.getSortedRowModel().rows.map((r) => {
                          return { rowId: r.id, ...r.original };
                        }),
                      );
                    }
                  }}
                  className="hover:bg-gray-100 focus:bg-gray-300"
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TableCell
                        key={cell.id}
                        // className='bg-danger-100'
                        // @ts-ignore
                        {...cell.column.columnDef.meta?.cellProps}
                      >
                        <FlexRender cell={cell} />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {!isBusy && (
        <PaginationBar
          searchResults={data}
          table={table}
          selectedRows={selectedRows}
          rowSelectionEnabled={rowSelection.enabled}
          first={{
            onClick: () => {
              setResults({
                ...results,
                pageIndex: 0,
              });
            },
          }}
          previous={{
            onClick: () => {
              setResults({
                ...results,
                pageIndex: results.pageIndex - 1,
              });
            },
          }}
          next={{
            onClick: () => {
              setResults({
                ...results,
                pageIndex: results.pageIndex + 1,
              });
            },
          }}
          last={{
            onClick: () => {
              setResults({
                ...results,
                pageIndex: results.totalPages - 1,
              });
            },
          }}
        />
      )}

      <div className={clsx(!queryParams.has('debug') && 'hidden', 'flex flex-row justify-center gap-4')}>
        <div>
          <button className="mb-2 rounded border p-2" onClick={() => console.info('selectedRows', selectedRows)}>
            Log `selectedRows` state
          </button>
        </div>
        <div>
          <button
            className="mb-2 rounded border p-2"
            onClick={() => console.info('table.getSelectedRowModel().flatRows', table.getSelectedRowModel().flatRows)}
          >
            Log table.getSelectedRowModel().flatRows
          </button>
        </div>
        <pre>{JSON.stringify(table.state.pagination, null, 2)}</pre>
      </div>
    </div>
  );
};

export default PaginatedManagerTable;
